import { Box, Button, Card, CardContent, CardHeader, Divider, Typography, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useGetProductsQuery, useEditProductMutation } from "services";
import { tokens } from "theme";
import dataGridStyles from "../../styles/dataGridStyles";

const EditGroup = () => {
  const theme = useTheme();
  const styles = dataGridStyles(theme.palette.mode);
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { groupId } = useParams();
  const [editProduct] = useEditProductMutation();

  // Fetch all products matching this groupId
  const { data, isLoading } = useGetProductsQuery({ params: { groupId } });
  const items = data?.items || data || [];

  // Grouped info — all sizes share same design
  const first = items[0] || {};
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});

  useEffect(() => {
    if (first?._id) {
      setEditingId(first._id);
      setDraft({
        name: first.name || "",
        sku: first.sku || "",
        description: first.description || "",
        originalPrice: first.prices?.b2cPrice?.originalPrice || 0,
        sellingPrice: first.prices?.b2cPrice?.sellingPrice || 0,
        discount: first.prices?.b2cPrice?.discount || 0,
      });
    }
  }, [first?._id]);

  const handleSave = async () => {
    if (!editingId) return;
    try {
      await editProduct({ id: editingId, body: draft }).unwrap();
      alert("✅ Saved!");
    } catch (e) {
      console.error(e);
      alert("❌ Failed to save: " + (e?.data?.message || e?.message));
    }
  };

  return (
    <Box m="20px">
      <CardHeader
        title="📦 Edit All Sizes (Group)"
        subheader={`Design: ${first?.name || ""} — Group ID: ${groupId} — ${items.length} size variant(s)`}
      />
      <Card sx={{ mb: 3 }}>
        <CardContent>
          {isLoading ? (
            <Typography>Loading products with group {groupId}...</Typography>
          ) : items.length === 0 ? (
            <Typography color="error">No products found for group {groupId}.</Typography>
          ) : (
            <>
              <Typography variant="subtitle1" gutterBottom>
                Common design info (applies to all sizes):
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 2, mb: 3 }}>
                <Box>
                  <Typography variant="caption">Name</Typography>
                  <input
                    value={draft.name || ""}
                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    style={{ width: "100%", padding: 8, border: "1px solid #ccc", borderRadius: 4 }}
                  />
                </Box>
                <Box>
                  <Typography variant="caption">SKU (base)</Typography>
                  <input
                    value={draft.sku || ""}
                    onChange={(e) => setDraft({ ...draft, sku: e.target.value })}
                    style={{ width: "100%", padding: 8, border: "1px solid #ccc", borderRadius: 4 }}
                  />
                </Box>
                <Box>
                  <Typography variant="caption">Description</Typography>
                  <input
                    value={draft.description || ""}
                    onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                    style={{ width: "100%", padding: 8, border: "1px solid #ccc", borderRadius: 4 }}
                  />
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" gutterBottom>
                Per-Size Variants:
              </Typography>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: colors.blueAccent[600], color: "white" }}>
                    <th style={{ padding: 8, textAlign: "left" }}>Size</th>
                    <th style={{ padding: 8, textAlign: "left" }}>SKU</th>
                    <th style={{ padding: 8, textAlign: "left" }}>Price</th>
                    <th style={{ padding: 8, textAlign: "left" }}>Edit</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((p, i) => (
                    <tr key={p._id} style={{ background: i % 2 ? "#f9f9f9" : "white" }}>
                      <td style={{ padding: 8 }}>{p.sizes?.[0]?.size || "—"}</td>
                      <td style={{ padding: 8 }}>{p.sku}</td>
                      <td style={{ padding: 8 }}>₹{p.prices?.b2cPrice?.sellingPrice}</td>
                      <td style={{ padding: 8 }}>
                        <Button
                          size="small"
                          variant={editingId === p._id ? "contained" : "outlined"}
                          onClick={() => {
                            setEditingId(p._id);
                            setDraft({
                              name: p.name || "",
                              sku: p.sku || "",
                              description: p.description || "",
                              originalPrice: p.prices?.b2cPrice?.originalPrice || 0,
                              sellingPrice: p.prices?.b2cPrice?.sellingPrice || 0,
                              discount: p.prices?.b2cPrice?.discount || 0,
                            });
                          }}
                        >
                          {editingId === p._id ? "Editing" : "Edit"}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
                <Button variant="contained" onClick={handleSave}>
                  💾 Save Changes
                </Button>
                <Button variant="outlined" onClick={() => navigate("/products")}>
                  Back to Products
                </Button>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default EditGroup;